import React, { useState, useEffect } from 'react';
import { Brain, Moon, Sun, TrendingUp, Zap, ThumbsUp, Coffee, Battery, Activity } from 'lucide-react';
import InputCard from '../common/InputCard';
import TipCard from '../common/TipCard';
import ProgressBar from '../common/ProgressBar';
import { motion } from 'framer-motion';

const DecisionFatigueCalculator = () => {
  const [decisions, setDecisions] = useState(50);
  const [sleepHours, setSleepHours] = useState(7);
  const [screenTime, setScreenTime] = useState(6);
  const [workDecisions, setWorkDecisions] = useState(30);
  const [personalDecisions, setPersonalDecisions] = useState(20);
  const [fatigueScore, setFatigueScore] = useState(0);
  const [fatigueLevel, setFatigueLevel] = useState({ level: "Low", color: "text-green-600", bg: "bg-green-100", emoji: "🧠✨" });

  useEffect(() => {
    let score = 0;
    // Decision weight: more decisions = higher fatigue
    score += Math.min(45, decisions * 0.55);
    
    // Sleep: less than 7 hours increases fatigue significantly
    if (sleepHours < 7) score += (7 - sleepHours) * 9;
    if (sleepHours > 9) score -= 5; // Too much sleep can also cause fatigue
    
    // Screen time: more than 5 hours increases fatigue
    if (screenTime > 5) score += (screenTime - 5) * 3.5;
    
    // Work-personal balance: if work decisions are > 2x personal
    if (workDecisions > personalDecisions * 2) score += 15;
    
    // Decision variety penalty
    const totalDecisions = workDecisions + personalDecisions;
    if (totalDecisions > 0 && Math.abs(workDecisions - personalDecisions) > totalDecisions * 0.6) {
      score += 8;
    }
    
    const finalScore = Math.min(100, Math.max(0, Math.round(score)));
    setFatigueScore(finalScore);
    
    if (finalScore < 30) {
      setFatigueLevel({ level: "Low Fatigue", color: "text-green-600", bg: "bg-green-100", emoji: "🧠✨", message: "Your mental energy is well-managed!" });
    } else if (finalScore < 60) {
      setFatigueLevel({ level: "Medium Fatigue", color: "text-yellow-600", bg: "bg-yellow-100", emoji: "😐⚡", message: "Consider simplifying your daily decisions" });
    } else {
      setFatigueLevel({ level: "High Fatigue", color: "text-red-600", bg: "bg-red-100", emoji: "😫💤", message: "Decision fatigue is affecting you - take a break!" });
    }
  }, [decisions, sleepHours, screenTime, workDecisions, personalDecisions]);

  const getRecoveryTips = () => {
    const tips = [];
    if (fatigueScore > 50) {
      if (decisions > 60) tips.push("Reduce small decisions - automate or delegate when possible");
      if (sleepHours < 7) tips.push("Prioritize getting 7-8 hours of quality sleep");
      if (screenTime > 6) tips.push("Take regular screen breaks every 90 minutes");
      if (workDecisions > personalDecisions * 1.5) tips.push("Create better work-life balance");
    }
    return tips;
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <InputCard title="Daily Mental Load Factors" icon={Brain} delay={0.1}>
          <div className="space-y-5">
            <div>
              <div className="flex justify-between mb-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <TrendingUp size={16} /> Total Decisions per Day
                </label>
                <span className="text-sm font-semibold text-blue-600">{decisions} decisions</span>
              </div>
              <input 
                type="range" 
                min="10" 
                max="150" 
                value={decisions} 
                onChange={(e) => setDecisions(parseInt(e.target.value))} 
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>Low (10)</span>
                <span>Average (80)</span>
                <span>High (150+)</span>
              </div>
            </div>

            <div>
              <div className="flex justify-between mb-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Moon size={16} /> Sleep Hours
                </label>
                <span className={`text-sm font-semibold ${sleepHours < 6 ? "text-red-600" : sleepHours < 7 ? "text-yellow-600" : "text-green-600"}`}>
                  {sleepHours} hours
                </span>
              </div>
              <input 
                type="range" 
                min="3" 
                max="10" 
                step="0.5" 
                value={sleepHours} 
                onChange={(e) => setSleepHours(parseFloat(e.target.value))} 
                className="w-full"
              />
            </div>

            <div>
              <div className="flex justify-between mb-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Sun size={16} /> Daily Screen Time
                </label>
                <span className={`text-sm font-semibold ${screenTime > 8 ? "text-red-600" : screenTime > 5 ? "text-yellow-600" : "text-green-600"}`}>
                  {screenTime} hours
                </span>
              </div>
              <input 
                type="range" 
                min="1" 
                max="14" 
                value={screenTime} 
                onChange={(e) => setScreenTime(parseInt(e.target.value))} 
                className="w-full"
              />
            </div>
          </div>
        </InputCard>

        <InputCard title="Decision Breakdown" icon={Activity} delay={0.2}>
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Work-Related Decisions</label>
              <input 
                type="range" 
                min="0" 
                max="80" 
                value={workDecisions} 
                onChange={(e) => setWorkDecisions(parseInt(e.target.value))} 
                className="w-full"
              />
              <div className="text-right text-sm text-blue-600 mt-1">{workDecisions} decisions/day</div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Personal Decisions</label>
              <input 
                type="range" 
                min="0" 
                max="80" 
                value={personalDecisions} 
                onChange={(e) => setPersonalDecisions(parseInt(e.target.value))} 
                className="w-full"
              />
              <div className="text-right text-sm text-green-600 mt-1">{personalDecisions} decisions/day</div>
            </div>

            <div className="mt-4 p-4 bg-gray-50 rounded-xl">
              <div className="flex justify-between mb-2 text-sm">
                <span>Work/Personal Ratio</span>
                <span className="font-semibold">{Math.round(workDecisions / Math.max(1, personalDecisions))}:1</span>
              </div>
              <ProgressBar 
                value={workDecisions} 
                max={workDecisions + personalDecisions} 
                color="blue" 
              />
            </div>
          </div>
        </InputCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.div 
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="bg-white rounded-xl shadow-sm p-6 border text-center"
        >
          <div className="relative w-40 h-40 mx-auto mb-4">
            <svg className="w-full h-full transform -rotate-90">
              <circle cx="80" cy="80" r="70" stroke="#e5e7eb" strokeWidth="12" fill="none" />
              <circle 
                cx="80" cy="80" r="70" 
                stroke={fatigueScore < 30 ? "#10b981" : fatigueScore < 60 ? "#eab308" : "#ef4444"} 
                strokeWidth="12" 
                fill="none" 
                strokeDasharray={`${(fatigueScore / 100) * 439.82} 439.82`} 
                strokeLinecap="round"
                className="transition-all duration-700"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-3xl mb-1">{fatigueLevel.emoji}</span>
              <span className="text-3xl font-bold text-gray-800">{fatigueScore}</span>
              <span className="text-xs text-gray-400">/100</span>
            </div>
          </div>
          <div className={`inline-block px-4 py-1.5 rounded-full ${fatigueLevel.bg} ${fatigueLevel.color} font-semibold text-base mb-2`}>
            {fatigueLevel.level}
          </div>
          <p className="text-sm text-gray-500">{fatigueLevel.message}</p>
        </motion.div>

        <div className="space-y-4">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-5">
            <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <Battery size={18} className="text-blue-600" />
              Mental Energy Status
            </h3>
            <div className="space-y-3">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Decision Capacity</span>
                  <span>{Math.max(0, 100 - fatigueScore)}%</span>
                </div>
                <ProgressBar value={100 - fatigueScore} max={100} color="green" />
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Willpower Remaining</span>
                  <span>{Math.max(0, Math.round((100 - fatigueScore) * 0.9))}%</span>
                </div>
                <ProgressBar value={(100 - fatigueScore) * 0.9} max={100} color="blue" />
              </div>
            </div>
          </div>

          {getRecoveryTips().length > 0 && (
            <div className="bg-orange-50 rounded-xl p-5 border border-orange-100">
              <h3 className="font-semibold text-orange-800 mb-2 flex items-center gap-2">
                <Zap size={18} />
                Recovery Suggestions
              </h3>
              {getRecoveryTips().map((tip, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm text-orange-700 mt-2">
                  <Coffee size={14} />
                  <span>{tip}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <TipCard icon={ThumbsUp} title="Batch Decisions" text="Group similar decisions together to save mental energy" color="blue" />
        <TipCard icon={Moon} title="Prioritize Sleep" text="Quality sleep restores decision-making capacity" color="green" />
        <TipCard icon={Zap} title="Take Breaks" text="Short breaks every 90 minutes prevent mental fatigue" color="purple" />
      </div>
    </motion.div>
  );
};

export default DecisionFatigueCalculator;